import type {
  ExposureRegistryEntry,
  ImpactBundleTrail,
  SecondOrderSnapshot,
  SecondOrderStateResponse,
} from '@akem/shared';
import { getApiEndpoint } from '../config/api.js';

export interface SecondOrderSnapshotResult {
  snapshot: SecondOrderSnapshot;
  trails: ImpactBundleTrail[];
  etag?: string | null;
}

export interface SecondOrderRequestOptions {
  limit?: number;
  metric?: string;
}

export interface ExposureHistoryEntry {
  instrumentId: string;
  version: string;
  schemaVersion?: string;
  updatedAt?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  expiry?: string | null;
  sources?: string[];
}

export interface ExposureEntryResponse {
  exposure: ExposureRegistryEntry | null;
  history: ExposureHistoryEntry[];
}

const buildInstrumentId = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.toLowerCase().startsWith('instrument:')) {
    return trimmed;
  }
  return `instrument:${trimmed.toUpperCase()}`;
};

export function resolveInstrumentId(input?: {
  instrumentId?: string | null;
  profileId?: string | null;
  ticker?: string | null;
}): string | null {
  if (!input) return null;
  const candidates = [input.instrumentId, input.ticker, input.profileId];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    return buildInstrumentId(trimmed);
  }
  return null;
}

export async function getSecondOrderSnapshot(
  instrumentId: string,
  options?: SecondOrderRequestOptions,
  fetchOptions?: { signal?: AbortSignal }
): Promise<SecondOrderSnapshotResult | null> {
  const resolved = buildInstrumentId(instrumentId);
  const url = new URL(
    `${getApiEndpoint()}/intelligence/second-order/${encodeURIComponent(resolved)}`
  );
  if (options?.limit) {
    url.searchParams.set('limit', String(Math.max(1, Math.floor(options.limit))));
  }
  if (options?.metric) {
    url.searchParams.set('metric', options.metric);
  }

  const res = await fetch(url.toString(), { signal: fetchOptions?.signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`second-order snapshot failed: ${res.status}`);
  }
  const json = (await res.json()) as SecondOrderStateResponse;
  return {
    snapshot: json.snapshot as SecondOrderSnapshot,
    trails: json.trails as ImpactBundleTrail[],
    etag: res.headers.get('etag'),
  };
}

export interface ExposureEntryRequestOptions {
  signal?: AbortSignal;
  includeHistory?: boolean;
  historyLimit?: number;
}

export async function getExposureEntry(
  instrumentId: string,
  options?: ExposureEntryRequestOptions
): Promise<ExposureEntryResponse | null> {
  const resolved = buildInstrumentId(instrumentId);
  const url = new URL(
    `${getApiEndpoint()}/intelligence/exposures/${encodeURIComponent(resolved)}`
  );
  if (options?.includeHistory) {
    url.searchParams.set('history', '1');
    if (options?.historyLimit) {
      const limit = Math.max(1, Math.min(Math.floor(options.historyLimit), 25));
      url.searchParams.set('historyLimit', String(limit));
    }
  }

  const res = await fetch(url.toString(), { signal: options?.signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`exposure lookup failed: ${res.status}`);
  }
  const json = await res.json();
  return {
    exposure: (json?.exposure as ExposureRegistryEntry) ?? null,
    history: Array.isArray(json?.history)
      ? (json.history as ExposureHistoryEntry[])
      : [],
  };
}

export function splitTrails(
  trails: ImpactBundleTrail[]
): {
  anchored: ImpactBundleTrail[];
  signalOnly: ImpactBundleTrail[];
} {
  const anchored: ImpactBundleTrail[] = [];
  const signalOnly: ImpactBundleTrail[] = [];
  for (const trail of trails) {
    if (trail?.bundle?.anchored_by_bedrock) {
      anchored.push(trail);
    } else {
      signalOnly.push(trail);
    }
  }
  return { anchored, signalOnly };
}

export const formatConfidence = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '–';
  return `${Math.round(value * 100)}%`;
};

export const formatMagnitude = (
  magnitude: number,
  units: string | undefined
): string => {
  const rounded = Math.round(magnitude * 100) / 100;
  if (!units) return `${rounded}`;
  return units === 'bps' ? `${rounded} bps` : `${rounded} ${units}`;
};

export const summarizeExplanation = (
  trail: ImpactBundleTrail,
  maxSteps = 2
): string => {
  if (!Array.isArray(trail.bundle?.explanation_path)) return '';
  const steps = trail.bundle.explanation_path
    .slice(0, maxSteps)
    .map((step: any) => {
      if (step?.event) return step.event;
      if (step?.note) return step.note;
      if (step?.edge) return step.edge;
      return null;
    })
    .filter((value): value is string => Boolean(value));
  return steps.join(' → ');
};
